import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, CheckCircle, XCircle } from "lucide-react";
import { Header } from "../../components/layout/Header";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";

// ── Data pools ────────────────────────────────────────────────────────────────

const HORSE_POOL = [
  "Fierceness",
  "Mystik Dan",
  "Sierra Leone",
  "Forever Young",
  "Track Phantom",
  "Catching Freedom",
  "Just Steel",
  "Dornoch",
  "Resilience",
  "Honor Marie",
  "Determined Bravado",
  "T O Password",
  "Catalytic",
  "Endlessly",
  "Stronghold",
  "Locked",
  "Antiquarian",
  "Pure Water",
  "Grand Mo the First",
  "River Thames",
];

const BETTOR_POOL = [
  "Jason G",
  "Jeremy F",
  "Sarah M",
  "Mike T",
  "Lisa K",
  "Chris B",
  "Megan R",
  "Dave P",
  "Amy W",
  "Tom H",
  "Rachel N",
  "Brian S",
  "Katie L",
  "Nate C",
  "Jess V",
  "Dan O",
  "Monica E",
  "Tyler Z",
  "Priya A",
  "Sean M",
];

const EVENT_NAME = "Kentucky Derby 2026 – Dev Test";

// ── Helpers ───────────────────────────────────────────────────────────────────

function randInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randDollar(min: number, max: number) {
  const step = 5;
  const lo = Math.ceil(min / step) * step;
  const hi = Math.floor(max / step) * step;
  if (lo > hi) return lo;
  const steps = Math.floor((hi - lo) / step);
  return lo + randInt(0, steps) * step;
}

function pickUnique<T>(arr: T[], n: number): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = randInt(0, i);
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, Math.min(n, copy.length));
}

interface GeneratedBet {
  bettorName: string;
  horseNumber: number;
  horseName: string;
  participantId: string;
  winAmount: number;
  placeAmount: number;
  showAmount: number;
}

function generateBets(
  bettorCount: number,
  minBets: number,
  maxBets: number,
  minDollar: number,
  maxDollar: number,
  horseCount: number,
): GeneratedBet[] {
  const horses = HORSE_POOL.slice(0, horseCount);
  const bettors = pickUnique(BETTOR_POOL, bettorCount);
  const result: GeneratedBet[] = [];

  for (const bettorName of bettors) {
    const betCount = randInt(minBets, maxBets);
    const chosenHorses = pickUnique(
      horses.map((name, i) => ({ name, num: i + 1 })),
      betCount,
    );

    for (const horse of chosenHorses) {
      const hasWin = Math.random() < 0.75;
      const hasPlace = Math.random() < 0.6;
      const hasShow = Math.random() < 0.4;
      const types = [hasWin, hasPlace, hasShow];
      if (!types.some(Boolean)) types[randInt(0, 2)] = true;

      result.push({
        bettorName,
        horseNumber: horse.num,
        horseName: horse.name,
        // participantId injected after seeding — set to empty string as placeholder
        participantId: "",
        winAmount: types[0] ? randDollar(minDollar, maxDollar) : 0,
        placeAmount: types[1] ? randDollar(minDollar, maxDollar) : 0,
        showAmount: types[2] ? randDollar(minDollar, maxDollar) : 0,
      });
    }
  }

  return result;
}

// ── Types ─────────────────────────────────────────────────────────────────────

type LogEntry = { ok: boolean; msg: string };

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className='text-xs font-semibold text-gray-400 uppercase tracking-wide pt-1'>
      {children}
    </p>
  );
}

function NumField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className='flex items-center justify-between'>
      <span className='text-sm text-gray-600'>{label}</span>
      <input
        type='number'
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
        }}
        className='w-20 text-right border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400'
      />
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DevGeneratePariMutuelPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [bettorCount, setBettorCount] = useState(5);
  const [minBets, setMinBets] = useState(2);
  const [maxBets, setMaxBets] = useState(5);
  const [minDollar, setMinDollar] = useState(5);
  const [maxDollar, setMaxDollar] = useState(25);
  const [horseCount, setHorseCount] = useState(18);
  const [addSampleBets, setAddSampleBets] = useState(true);

  const [generating, setGenerating] = useState(false);
  const [log, setLog] = useState<LogEntry[]>([]);

  const estBets = useMemo(() => {
    const avg = (minBets + maxBets) / 2;
    return Math.round(bettorCount * avg);
  }, [bettorCount, minBets, maxBets]);

  function handleMinBets(v: number) {
    setMinBets(v);
    if (v > maxBets) setMaxBets(v);
  }
  function handleMaxBets(v: number) {
    setMaxBets(v);
    if (v < minBets) setMinBets(v);
  }
  function handleMinDollar(v: number) {
    setMinDollar(v);
    if (v > maxDollar) setMaxDollar(v);
  }
  function handleMaxDollar(v: number) {
    setMaxDollar(v);
    if (v < minDollar) setMinDollar(v);
  }

  function addLog(ok: boolean, msg: string) {
    setLog((prev) => [...prev, { ok, msg }]);
  }

  async function handleGenerate() {
    if (!user) return;
    setLog([]);
    setGenerating(true);

    try {
      addLog(true, "Creating parimutuel event…");
      const eventRes = (await api.events.create({
        createdByName: user.displayName ?? "Dev User",
        eventType: "parimutuel-betting" as any,
        name: EVENT_NAME,
        notes: "Auto-generated dev test event",
        firstRound: {
          courseId: "dev",
          courseName: "Churchill Downs",
          teeBox: "N/A",
          date: new Date().toISOString(),
          gameConfig: {
            templateId: "parimutuel",
            teamMode: "individual",
            scoringMethod: "parimutuel",
          },
        },
      })) as { data?: { id: string } };

      const eventId = eventRes.data?.id;
      if (!eventId) throw new Error("No event ID returned");
      addLog(true, `Event created: ${eventId}`);

      const pmRes = (await api.pariMutuel.create({
        eventId,
        eventName: EVENT_NAME,
      })) as { data?: { id: string } };

      const pmId = pmRes.data?.id;
      if (!pmId) throw new Error("No parimutuel ID returned");
      addLog(true, `Parimutuel board created: ${pmId}`);

      // Seed participants first
      const horseNames = HORSE_POOL.slice(0, horseCount);
      addLog(true, `Seeding ${horseNames.length} participants…`);
      const participantIdByNum = new Map<number, string>();
      for (let i = 0; i < horseNames.length; i++) {
        const name = `${i + 1} - ${horseNames[i]}`;
        const pRes = (await api.pariMutuel.addParticipant(pmId, { name })) as {
          data?: { id: string; name: string };
        };
        const pid = pRes.data?.id;
        if (pid) participantIdByNum.set(i + 1, pid);
        addLog(true, `  ✓ Participant: ${name}`);
      }

      if (addSampleBets) {
        const bets = generateBets(
          bettorCount,
          minBets,
          maxBets,
          minDollar,
          maxDollar,
          horseCount,
        );
        addLog(
          true,
          `Adding ${bets.length} bets across ${bettorCount} bettors…`,
        );
        for (const bet of bets) {
          const participantId = participantIdByNum.get(bet.horseNumber) ?? "";
          await api.pariMutuel.addBet(pmId, { ...bet, participantId });
          const types = [
            bet.winAmount ? `W$${bet.winAmount}` : "",
            bet.placeAmount ? `P$${bet.placeAmount}` : "",
            bet.showAmount ? `S$${bet.showAmount}` : "",
          ]
            .filter(Boolean)
            .join(" ");
          addLog(
            true,
            `  ✓ ${bet.bettorName} → #${bet.horseNumber} ${bet.horseName} [${types}]`,
          );
        }
      }

      addLog(true, "Done! Navigating to event…");
      navigate(`/events/${eventId}/parimutuel`);
    } catch (err: any) {
      addLog(false, `Error: ${err.message ?? String(err)}`);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <>
      <Header title='Generate Parimutuel Event' showBack />
      <div className='px-4 py-4 space-y-4'>
        {/* Config card */}
        <Card>
          <label className='flex items-center gap-2 mb-4 cursor-pointer select-none'>
            <input
              type='checkbox'
              checked={addSampleBets}
              onChange={(e) => setAddSampleBets(e.target.checked)}
              className='w-4 h-4 accent-amber-500 rounded'
            />
            <span className='text-sm text-gray-700 font-medium'>
              Add randomized sample bets
            </span>
          </label>

          {addSampleBets && (
            <div className='space-y-3'>
              <SectionLabel>Field</SectionLabel>
              <NumField
                label='Horses in the field'
                value={horseCount}
                min={8}
                max={HORSE_POOL.length}
                onChange={setHorseCount}
              />

              <SectionLabel>Bettors</SectionLabel>
              <NumField
                label='Number of bettors'
                value={bettorCount}
                min={1}
                max={BETTOR_POOL.length}
                onChange={setBettorCount}
              />

              <SectionLabel>Bets per bettor</SectionLabel>
              <NumField
                label='Min bets'
                value={minBets}
                min={1}
                max={horseCount}
                onChange={handleMinBets}
              />
              <NumField
                label='Max bets'
                value={maxBets}
                min={1}
                max={horseCount}
                onChange={handleMaxBets}
              />

              <SectionLabel>Amount per bet type ($5 increments)</SectionLabel>
              <NumField
                label='Min amount ($)'
                value={minDollar}
                min={1}
                max={500}
                onChange={handleMinDollar}
              />
              <NumField
                label='Max amount ($)'
                value={maxDollar}
                min={5}
                max={500}
                onChange={handleMaxDollar}
              />

              <div className='pt-2 border-t border-gray-100 text-xs text-gray-400'>
                Est. ~{estBets} bets · Win/Place/Show each randomized per bet
              </div>
            </div>
          )}
        </Card>

        {/* Action */}
        <Button
          fullWidth
          loading={generating}
          onClick={handleGenerate}
          leftIcon={<Plus size={16} />}
        >
          Generate Event
        </Button>

        {/* Log output */}
        {log.length > 0 && (
          <Card>
            <div className='space-y-1 max-h-64 overflow-y-auto'>
              {log.map((entry, i) => (
                <div key={i} className='flex items-start gap-1.5 text-xs'>
                  {entry.ok ? (
                    <CheckCircle
                      size={13}
                      className='text-green-500 mt-0.5 shrink-0'
                    />
                  ) : (
                    <XCircle
                      size={13}
                      className='text-red-500 mt-0.5 shrink-0'
                    />
                  )}
                  <span
                    className={
                      entry.ok ? "text-gray-600" : "text-red-600 font-medium"
                    }
                  >
                    {entry.msg}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
